import React, { useState, useEffect } from 'react';
import "./formRender.css";
import { FormValidator } from './validate';

// Updated to use Tailwind grid classes instead
const getColumnsClass = (columns: string) => {
  if (columns === "2") return "grid grid-cols-2 gap-4";
  if (columns === "3") return "grid grid-cols-3 gap-4";
  return "";
};

const renderChildrenReadOnly = (formTemplate: any, formState: any, setFormState: any, onSubmitForm: any, depth = 0) => {
  const templateItems = Array.isArray(formTemplate) ? formTemplate : [];
  return templateItems.map((item: any, index: number) => {
    if (item.type === 'section') {
      const parent = depth ? '' : 'mb-6';
      const columnsClass = getColumnsClass(item.columns);

      return (
        <div key={index} className={`mb-4 ${parent}`}>
          <h3 className="text-sm font-medium text-gray-700 mb-2">{item.text || item.label}</h3>
          <div className={columnsClass}>
            {renderChildrenReadOnly(item.children, formState, setFormState, 1)}
          </div>
        </div>
      );
    }
    
    if (item.type === 'text') 
      return <div key={index} className="text-sm text-gray-600 mb-2">{item.text}</div>;

    if (item.type === 'bullet') 
      return <li key={index} className="text-sm text-gray-600">{item.text}</li>;

    if (item.type === 'bulletList') {
      return (
        <ul className="list-disc pl-5 mb-3 text-sm text-gray-600">
          {item.items.map((item: any, index: any) => {
            return <li key={index}>{item.text || item}</li>;
          })}
        </ul>
      );
    }

    if (item.type === 'paragraph') {
      return <p key={index} className="text-sm text-gray-600 mb-3">{item.text}</p>;
    }

    if (item.type === 'heading') {
      return <h2 key={index} className="text-base font-medium text-gray-700 mb-2">{item.text}</h2>;
    }

    if (item.type === 'textInput') {
      return (
        <div key={index} className="mb-3">
          <label id={`label${item.key}`} className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <div className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-gray-50">
            {formState[item.key] || '—'}
          </div>
        </div>
      );
    }

    if (item.type === 'staticInput') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <div className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-gray-50">
            {formState[item.key] || '—'}
          </div>
        </div>
      );
    }

    if (item.type === 'textAreaInput') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <div className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-gray-50 min-h-[60px]">
            {formState[item.key] || '—'}
          </div>
        </div>
      );
    }

    if (item.type === 'yesnoInput') {
      return (
        <div key={index} className="flex items-center mb-3">
          <div className="w-5 h-5 mr-2 border border-gray-300 rounded flex items-center justify-center bg-gray-50">
            {formState[item.key] ? "✓" : ""}
          </div>
          <label className="text-xs font-medium text-gray-600" htmlFor={item.key}>
            {item.label}
          </label>
        </div>
      );
    }

    if (item.type === 'listInput') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={`label${item.key}`}>
            {item.label}
          </label>
          <div className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-gray-50">
            {item.options.find((option: any) => option.value === formState[item.key])?.label || '—'}
          </div>
        </div>
      );
    }

    if (item.type === 'dateInput') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <div className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-gray-50">
            {formState[item.key] || '—'}
          </div>
        </div>
      );
    }

    if (item.type === 'repeater') {
      const items = formState[item.key] || [];
      return (
        <div key={index} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {item.label}
          </label>
          {items.length === 0 ? (
            <div className="text-sm text-gray-500 italic">No items added</div>
          ) : (
            <div className="space-y-3">
              {items.map((itemData: any, itemIndex: number) => (
                <div key={itemIndex} className="border border-gray-200 rounded p-3 bg-gray-50">
                  <div className="mb-2">
                    <span className="text-xs font-medium text-gray-600">Item {itemIndex + 1}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {item.fields.map((field: any, fieldIndex: number) => {
                      const renderFieldValue = () => {
                        if (field.type === 'listInput') {
                          const selectedOption = field.options?.find((option: any) => option.value === itemData[field.key]);
                          return selectedOption?.label || itemData[field.key] || '—';
                        }
                        if (field.type === 'yesnoInput') {
                          return itemData[field.key] ? 'Yes' : 'No';
                        }
                        return itemData[field.key] || '—';
                      };

                      return (
                        <div key={fieldIndex}>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            {field.label}
                          </label>
                          <div className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white">
                            {renderFieldValue()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'collapsible') {
      return (
        <div key={index} className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3 bg-gray-100 px-3 py-2 rounded print:bg-white print:border print:border-gray-300">
            {item.label}
          </h3>
          <div className="pl-4">
            {renderChildrenReadOnly(item.fields, formState, setFormState, onSubmitForm, depth + 1)}
          </div>
        </div>
      );
    }
    
    return <React.Fragment key={index} />;
  });
};

const renderChildren = (formTemplate: any, formState: any, handleChange: any, onSubmitForm: any, setFormState: any, depth = 0) => {
  const templateItems = Array.isArray(formTemplate) ? formTemplate : [];
  return templateItems.map((item: any, index: number) => {
    if (item.type === 'section') {
      const outerClass = item.outercss || "mb-4";
      const innerClass = item.innercss || getColumnsClass(item.columns);
      
      return (
        <div key={index} className={outerClass}>
          <h3 className="text-sm font-medium text-gray-700 mb-2">{item.label}</h3>
          <div className={innerClass}>
            {renderChildren(item.children, formState, handleChange, onSubmitForm, setFormState, depth + 1)}
          </div>
        </div>
      );
    }
    
    if (item.type === 'text') 
      return <div key={index} className="text-sm text-gray-600 mb-2">{item.text}</div>;

    if (item.type === 'bullet') 
      return <li key={index} className="text-sm text-gray-600">{item.text}</li>;

    if (item.type === 'bulletList') {
      return (
        <ul className="list-disc pl-5 mb-3 text-sm text-gray-600">
          {item.items.map((item: any, index: any) => {
            return <li key={index}>{item.text}</li>;
          })}
        </ul>
      );
    }

    if (item.type === 'paragraph') {
      return <p key={index} className="text-sm text-gray-600 mb-3">{item.text}</p>;
    }

    if (item.type === 'heading') {
      return <h2 key={index} className="text-base font-medium text-gray-700 mb-2">{item.text}</h2>;
    }

    if (item.type === 'textInput') {
      return (
        <div key={index} className="mb-3">
          <label id={`label${item.key}`} className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <input 
            name={item.key}
            type="text"
            id={item.key} 
            value={formState[item.key] || ''} 
            onChange={handleChange}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
          />
        </div>
      );
    }

    if (item.type === 'staticInput') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <input 
            name={item.key}
            type="text"
            id={item.key} 
            value={formState[item.key] || ''} 
            readOnly={true}
            onChange={() => {}}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 bg-gray-50"
          />
        </div>
      );
    }

    if (item.type === 'textAreaInput') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <textarea 
            id={item.key} 
            name={item.key}
            value={formState[item.key] || ''} 
            onChange={handleChange}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 min-h-[60px] focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
          />
        </div>
      );
    }

    if (item.type === 'yesnoInput') {
      return (
        <div key={index} className="flex items-center mb-3">
          <input 
            type="checkbox" 
            id={item.key} 
            name={item.key}
            checked={formState[item.key] == 1}
            onChange={handleChange}
            className="h-4 w-4 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
          />
          <label className="ml-2 text-xs font-medium text-gray-600" htmlFor={item.key}>
            {item.label}
          </label>
        </div>
      );
    }

    if (item.type === 'listInputDowndown') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={`label${item.key}`}>
            {item.label}
          </label>
          <select
            id={item.key}
            name={item.key}
            value={formState[item.key] || ''}
            onChange={handleChange}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
          >
            <option value={''}>-- Select --</option>
            {item.options.map((option: any, optionIndex: number) => (
              <option key={optionIndex} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (item.type === 'listInput') {
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={`label${item.key}`}>
            {item.label}
          </label>
          <select
            id={item.key}
            name={item.key}
            value={formState[item.key] || ''}
            onChange={handleChange}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
          >
            <option value={''}>-- Select --</option>
            {item.options.map((option: any, optionIndex: number) => (
              <option key={optionIndex} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (item.type === 'dateInput') {
      const formatedDate = (formState[item.key]) ? new Date(formState[item.key]).toISOString().split('T')[0] : '';
      return (
        <div key={index} className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={item.key}>
            {item.label}
          </label>
          <input 
            type="date" 
            name={item.key}
            id={item.key} 
            value={formatedDate}
            onChange={handleChange}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
          />
        </div>
      );
    }

    if (item.type === 'repeater') {
      const items = formState[item.key] || [];
      
      const addItem = () => {
        const newItem: any = {};
        item.fields.forEach((field: any) => {
          newItem[field.key] = '';
        });
        
        const newItems = [...items, newItem];
        setFormState({
          ...formState,
          [item.key]: newItems
        });
      };
      
      const removeItem = (itemIndex: number) => {
        const newItems = items.filter((_: any, idx: number) => idx !== itemIndex);
        setFormState({
          ...formState,
          [item.key]: newItems
        });
      };
      
      const updateItem = (itemIndex: number, fieldKey: string, value: any) => {
        const newItems = [...items];
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          [fieldKey]: value
        };
        setFormState({
          ...formState,
          [item.key]: newItems
        });
      };

      return (
        <div key={index} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {item.label}
          </label>
          <div className="space-y-3">
            {items.map((itemData: any, itemIndex: number) => (
              <div key={itemIndex} className="border border-gray-200 rounded p-3 bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-xs font-medium text-gray-600">Item {itemIndex + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeItem(itemIndex)}
                    className="text-red-600 hover:text-red-800 text-xs font-medium"
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {item.fields.map((field: any, fieldIndex: number) => (
                    <div key={fieldIndex}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {field.label}
                      </label>
                      {field.type === 'listInput' ? (
                        <select
                          value={itemData[field.key] || ''}
                          onChange={(e) => updateItem(itemIndex, field.key, e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
                        >
                          <option value="">-- Select --</option>
                          {field.options.map((option: any, optionIndex: number) => (
                            <option key={optionIndex} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : field.type === 'dateInput' ? (
                        <input
                          type="date"
                          value={itemData[field.key] || ''}
                          onChange={(e) => updateItem(itemIndex, field.key, e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
                        />
                      ) : field.type === 'yesnoInput' ? (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={itemData[field.key] == 1}
                            onChange={(e) => updateItem(itemIndex, field.key, e.target.checked ? 1 : 0)}
                            className="h-4 w-4 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                          />
                          <span className="ml-2 text-xs text-gray-600">Yes</span>
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={itemData[field.key] || ''}
                          onChange={(e) => updateItem(itemIndex, field.key, e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addItem}
              className="px-3 py-1.5 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-300"
            >
              Add {item.label}
            </button>
          </div>
        </div>
      );
    }

    if (item.type === 'collapsible') {
      const [collapsed, setCollapsed] = useState(!item.expanded);
      
      return (
        <div key={index} className="mb-4">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-3 bg-gray-100 px-3 py-2 rounded hover:bg-gray-200"
          >
            {item.label}
            <span className={`transition-transform ${collapsed ? 'rotate-0' : 'rotate-90'}`}>
              ▶
            </span>
          </button>
          {!collapsed && (
            <div className="pl-4 border-l-2 border-gray-200">
              {renderChildren(item.fields, formState, handleChange, onSubmitForm, setFormState, depth + 1)}
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'submit') {
      return (
        <div key={index} className="mb-3 mt-4">
          <button 
            type="submit" 
            onClick={onSubmitForm}
            className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            {item.label}
          </button>
        </div>
      );
    }
    
    return <React.Fragment key={index} />;
  });
};

export const FormRenderer = (params: any) => {
  const { formId, formTemplate, formState, setFormState, onSubmit, readOnly, unboxed } = params;
  
  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      FormValidator(formTemplate, formState);
      onSubmit();
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (event: any) => {
    const { id, value, type, checked } = event.target;
    
    if (type === 'checkbox') {
      setFormState({
        ...formState,
        [id]: checked ? 1 : 0,
      });
      return;
    }
    
    if (type === 'radio') {
      setFormState({
        ...formState,
        [event.target.name]: value,
      });
      return;
    }
    
    setFormState({
      ...formState,
      [id]: value,
    });
  };

  return (
    <form
      id={formId}
      className={unboxed ? 'document-form-unboxed' : ''}
      onSubmit={onSubmitForm}
    >
      <div className={unboxed ? '' : readOnly ? 'p-4' : 'p-4 border border-gray-200 rounded-md bg-white shadow-sm'}>
        {readOnly 
          ? renderChildrenReadOnly(formTemplate, formState, setFormState, () => {}) 
          : renderChildren(formTemplate, formState, handleChange, onSubmitForm, setFormState)
        }
      </div>
    </form>
  );
};

export const FormInput = FormRenderer;
